import {
  handleSubmitMultilingualForm,
  useCreateEshopPage,
  useGetEshopFormData,
  useUpdateEshopPage,
} from "@/api/cms";
import ArticleList from "@/components/cms/e-shop/ArticleList";
import { EshopForm } from "@/components/cms/e-shop/EshopPageForm";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import { useAuth } from "@/context/auth/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  constructLocaleDict,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import { cmsPageSchema } from "@/lib/cms/cms-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = multilingualFieldSchema(
  cmsPageSchema.pick({ pageName: true, published: true }),
);

type FormData = z.infer<typeof formSchema>;

const EshopCMS = () => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();

  const { data: eShopData } = useGetEshopFormData();
  const update = useUpdateEshopPage();
  const create = useCreateEshopPage();

  const defaultValues: FormData = useMemo(
    () =>
      constructLocaleDict((lang) => {
        const eShop = eShopData?.[lang];
        return {
          published: eShop?.published ?? false,
          pageName: eShop?.pageName ?? "",
        };
      }),
    [eShopData],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const { reset } = form;

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const { toast } = useToast();

  const onSubmit = (submittedData: FormData) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    Object.keys(form.formState.dirtyFields).forEach((key) => {
      const localeData = submittedData[key as SupportedLocale];
      const baseData = eShopData?.[key as SupportedLocale];
      if (!localeData) return;

      const mutation = baseData?.content.id ? update : create;

      const { pageName, published, ...content } = localeData;

      mutation.mutate(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: {
            pageName,
            published,
            content,
          },
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("contactSuccessMessage"),
              duration: 1000,
              variant: "success",
            });
          },
          onError: (error) => {
            console.error("Error", error);
            toast({
              title: t("error"),
              description: t("contactErrorMessage"),
              variant: "error",
              duration: 1000,
            });
          },
        },
      );
    });
  };

  return (
    <CMSLayout
      pageName={t("cmsTabEshop")}
      formIds={["e-shop"]}
      className="flex flex-col"
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmitMultilingualForm(form, eShopData, onSubmit)}
          id="e-shop"
          className="flex w-full gap-4"
        >
          <DoubleMultilingualField
            className="w-full"
            corner={(lang) => (
              <Switch dispLabel={false} name={`${lang}.published`} />
            )}
            render={(lang) => <EshopForm language={lang} />}
          />
        </form>
      </FormProvider>
      <ArticleList />
    </CMSLayout>
  );
};

export default EshopCMS;
